import { useState } from "react";
import { Navigate, NavLink, useLocation, useOutletContext } from "react-router-dom";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { onboardingPath, rolesPath, usersPath } from "../../lib/api-paths.js";
import { env } from "../../lib/env.js";
import type {
  OrgOnboarding,
  PublicRole,
  UsersListResponse,
} from "../../lib/api-types.js";
import { InlineLoading } from "../../components/Spinner.js";
import type { OrgDetailContext } from "./context.js";
import { cn } from "../../lib/cn.js";

/**
 * 「开始使用」— 企业管理员首登引导（2026-07-04 用户确认的 4 项流程编排）：
 *  ① 搭建组织（飞书自动同步 / 手动建角色 两条路径并列）
 *  ② 员工账号（建号绑角色 + 启用 DOSIA）
 *  ③ 邀请员工（下载入口 env 可配 + 可复制邀请话术，无状态检测）
 *  ④ 进阶配置（可选，不计进度）
 * org-admin 从组织 index 进入时：①② 全完成 → 直接放行到基本信息；
 * 否则落本页。页面常驻导航可随时回看。
 */

interface StepState {
  done: boolean;
  loading: boolean;
}

export function GettingStarted(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const location = useLocation();
  const fromIndex =
    (location.state as { fromIndex?: boolean } | null)?.fromIndex === true;
  const [copied, setCopied] = useState(false);

  const { data: onboarding, loading: onboardingLoading } =
    useApiQuery<OrgOnboarding>(onboardingPath(org.code), [org.code]);
  const { data: roles, loading: rolesLoading } = useApiQuery<PublicRole[]>(
    rolesPath(org.code),
    [org.code],
  );
  const { data: users, loading: usersLoading } = useApiQuery<UsersListResponse>(
    usersPath(org.code, "?pageSize=200"),
    [org.code],
  );

  // ① 搭建：飞书路径（向导完成 / 已配飞书凭据）或 手动路径（admin 之外还有业务角色）
  const businessRoles = (roles ?? []).filter((r) => r.code !== "admin");
  const setup: StepState = {
    done:
      onboarding?.completedAt != null ||
      Boolean(org.feishu?.appId) ||
      businessRoles.length > 0,
    loading: onboardingLoading || rolesLoading,
  };
  // ② 员工：至少 1 名启用 DOSIA 的员工
  const enabledUsers = (users?.rows ?? []).filter((u) => u.enableDosia);
  const staff: StepState = {
    done: enabledUsers.length > 0,
    loading: usersLoading,
  };

  const allDone = setup.done && staff.done;
  const anyLoading = setup.loading || staff.loading;

  // 从组织 index 重定向进来且核心项全完成 → 不打扰，直接去基本信息
  if (fromIndex && !anyLoading && allDone) {
    return <Navigate to="../basic-info" replace />;
  }

  const inviteText = [
    `我们已开通 DOSIA（数字生活自动驾驶 AI 伴侣），请按以下步骤开始使用：`,
    `1. 下载安装 DOSIA 桌面端：${env.DOWNLOAD_URL}`,
    `2. 打开 DOSIA，选择「企业登录」`,
    `3. 组织代号填 ${org.code}，用户名和初始密码找管理员领取`,
    `登录后即可开始使用，有问题联系管理员。`,
  ].join("\n");

  const handleCopy = (): void => {
    void navigator.clipboard.writeText(inviteText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <h2 className="text-base font-semibold text-dosia-text">
          {allDone ? "初始配置已完成" : "开始使用 DOSIA"}
        </h2>
        <p className="text-sm text-dosia-text-secondary mt-0.5">
          {allDone
            ? "各环节就绪。本页保留在导航中，可随时回看邀请话术或补充配置。"
            : "按下面的顺序完成配置，贵司员工即可开始使用 DOSIA。"}
        </p>
      </div>

      {anyLoading ? <InlineLoading /> : null}

      {/* ① 搭建组织 */}
      <StepCard
        index={1}
        title="搭建组织（角色与部门）"
        done={setup.done}
        loading={setup.loading}
      >
        <p className="text-sm text-dosia-text-secondary">两条路径，任选其一：</p>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <PathCard
            title="使用飞书（推荐）"
            desc="接入飞书自建应用 → 同步部门通讯录 → AI 按部门推荐角色划分并分配员工"
            to="../onboarding"
            action="进入组织搭建向导"
          />
          <PathCard
            title="手动配置"
            desc="不用飞书：手动创建业务角色（如 策划 / 内容 / 媒介），再到用户页逐个建号"
            to="../roles"
            action="去创建角色"
          />
        </div>
      </StepCard>

      {/* ② 员工账号 */}
      <StepCard
        index={2}
        title="创建员工账号并启用 DOSIA"
        done={staff.done}
        loading={staff.loading}
      >
        <p className="text-sm text-dosia-text-secondary">
          在用户页为员工建号（用户名 + 初始密码）、绑定角色，并勾选「启用
          DOSIA」。
          {staff.done
            ? ` 当前已启用 ${enabledUsers.length} 名员工。`
            : " 尚无启用 DOSIA 的员工。"}
        </p>
        <NavLink
          to="../users"
          className="mt-2 inline-block rounded bg-dosia-accent px-3 py-1.5 text-sm font-medium text-dosia-bg-light hover:bg-dosia-accent-hover"
        >
          去管理用户
        </NavLink>
      </StepCard>

      {/* ③ 邀请员工 */}
      <StepCard index={3} title="邀请员工安装并登录" done={false} hideStatus>
        <p className="text-sm text-dosia-text-secondary">
          把下面的话术发给员工（含下载地址与组织代号），员工在 DOSIA
          桌面端选「企业登录」即可。
        </p>
        <pre className="mt-2 whitespace-pre-wrap rounded-md border border-dosia-border bg-dosia-bg-input p-3 text-xs text-dosia-text font-mono">
          {inviteText}
        </pre>
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="rounded bg-dosia-accent px-3 py-1.5 text-sm font-medium text-dosia-bg-light hover:bg-dosia-accent-hover"
          >
            {copied ? "已复制 ✓" : "复制邀请话术"}
          </button>
          <a
            href={env.DOWNLOAD_URL}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-dosia-accent hover:underline"
          >
            下载页 ↗
          </a>
        </div>
      </StepCard>

      {/* ④ 进阶（可选） */}
      <StepCard index={4} title="进阶配置（可选）" done={false} hideStatus>
        <ul className="text-sm text-dosia-text-secondary space-y-1">
          <li>
            · <NavLink to="../leadership" className="text-dosia-accent hover:underline">审批人</NavLink>
            ：指定各角色的部门审批人（审员工提交的技能 / MCP）
          </li>
          <li>
            · <NavLink to="../roles" className="text-dosia-accent hover:underline">角色策略</NavLink>
            ：按角色配置模型、单会话预算上限、内置 Agent 可见性
          </li>
          <li>
            · <NavLink to="../llm-config" className="text-dosia-accent hover:underline">LLM 配置</NavLink>
            ：默认走 DOSIA 平台模型服务，仅自带模型时需要配置
          </li>
        </ul>
      </StepCard>
    </div>
  );
}

function StepCard({
  index,
  title,
  done,
  loading = false,
  hideStatus = false,
  children,
}: {
  index: number;
  title: string;
  done: boolean;
  loading?: boolean;
  hideStatus?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-dosia-border bg-dosia-bg-light p-4">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
            done
              ? "bg-emerald-400/60 text-dosia-bg-light"
              : "border border-dosia-border text-dosia-text-secondary",
          )}
        >
          {done ? "✓" : index}
        </span>
        <h3 className="text-sm font-medium text-dosia-text">{title}</h3>
        {hideStatus || loading ? null : (
          <span className="ml-auto text-xs text-dosia-text-dim">
            {done ? "已完成" : "待完成"}
          </span>
        )}
      </div>
      <div className="mt-2 pl-8">{children}</div>
    </div>
  );
}

function PathCard({
  title,
  desc,
  to,
  action,
}: {
  title: string;
  desc: string;
  to: string;
  action: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-dosia-border p-3 flex flex-col gap-1.5">
      <p className="text-sm font-medium text-dosia-text">{title}</p>
      <p className="text-xs text-dosia-text-secondary flex-1">{desc}</p>
      <NavLink
        to={to}
        className="text-sm text-dosia-accent hover:underline self-start"
      >
        {action} →
      </NavLink>
    </div>
  );
}
